// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the loading indicator. Small is three typing dots in the current text
// colour, for buttons and rows. Medium and large are the logo: its two halves
// loosen into arcs, spin, and settle back into the mark, the same motion the
// boot splash in index.html uses.

import styles from '@app/features/ui/components/KonvoLoader.module.css';
import {
	halfPath,
	loaderFrame,
	MARK_COLORS,
	MARK_HALVES,
	MARK_PATHS,
	type MarkHalf,
} from '@app/features/ui/components/KonvoMarkMotion';
import {clsx} from 'clsx';
import {useEffect, useRef, useState} from 'react';

export type KonvoLoaderSize = 'small' | 'medium' | 'large';

const SIZE_CLASS: Record<KonvoLoaderSize, string> = {
	small: styles.small,
	medium: styles.medium,
	large: styles.large,
};

// Outline samples per half; more for the larger mark so curves stay smooth.
const SAMPLES: Record<'medium' | 'large', number> = {medium: 96, large: 160};

// One animation frame loop drives every mounted loader, and all of them read the
// same clock, so loaders on screen together move in step.
type FrameListener = (seconds: number) => void;
const listeners = new Set<FrameListener>();
let frameRequest = 0;

function tick(now: number) {
	for (const listener of listeners) listener(now / 1000);
	frameRequest = listeners.size ? requestAnimationFrame(tick) : 0;
}

function subscribe(listener: FrameListener): () => void {
	listeners.add(listener);
	if (!frameRequest) frameRequest = requestAnimationFrame(tick);
	return () => {
		listeners.delete(listener);
		if (!listeners.size && frameRequest) {
			cancelAnimationFrame(frameRequest);
			frameRequest = 0;
		}
	};
}

// The app mirrors its reduced motion setting onto <html> (see App.tsx); the media query covers the
// moments before that runs. Read once per loader: loaders are short-lived.
function prefersStill(): boolean {
	if (document.documentElement.classList.contains('reduced-motion')) return true;
	return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

function Dots({className}: {className?: string}) {
	return (
		<span className={clsx(styles.dots, className)}>
			<span className={styles.dot} />
			<span className={styles.dot} />
			<span className={styles.dot} />
		</span>
	);
}

function MarkLoader({samples}: {samples: number}) {
	const groupRef = useRef<SVGGElement>(null);
	const pathRefs = useRef<Partial<Record<MarkHalf, SVGPathElement | null>>>({});
	const [animate] = useState(() => !prefersStill());

	useEffect(() => {
		if (!animate) return;
		return subscribe((seconds) => {
			const frame = loaderFrame(seconds);
			groupRef.current?.setAttribute(
				'transform',
				`rotate(${(frame.rotation % 360).toFixed(2)} 256 256) translate(256 256) scale(${frame.scale.toFixed(4)}) translate(-256 -256)`,
			);
			for (const half of MARK_HALVES) {
				pathRefs.current[half]?.setAttribute('d', halfPath(half, frame, samples) ?? MARK_PATHS[half]);
			}
		});
	}, [animate, samples]);

	return (
		<svg className={clsx(styles.mark, !animate && styles.markStill)} viewBox="0 0 512 512" aria-hidden="true">
			<g ref={groupRef}>
				{MARK_HALVES.map((half) => (
					<path
						key={half}
						ref={(node) => {
							pathRefs.current[half] = node;
						}}
						d={MARK_PATHS[half]}
						fill={MARK_COLORS[half]}
					/>
				))}
			</g>
		</svg>
	);
}

export function KonvoLoader({
	size = 'medium',
	className,
	label,
	'data-flx': dataFlx,
}: {
	size?: KonvoLoaderSize;
	className?: string;
	label?: string;
	'data-flx'?: string;
}) {
	const a11y = label ? ({role: 'status', 'aria-label': label} as const) : ({'aria-hidden': true} as const);
	return (
		<span
			className={clsx(styles.loader, SIZE_CLASS[size], className)}
			{...a11y}
			data-flx={dataFlx ?? 'ui.konvo-loader'}
		>
			{size === 'small' ? <Dots /> : <MarkLoader samples={SAMPLES[size]} />}
		</span>
	);
}
